import { useEffect, useState, useRef } from "react";
import { isDesktop, isIOS, isMobileOnly } from "react-device-detect";
import screenfull from 'screenfull';

import {
  Backdrop,
  ControlsBottom,
  LeftControls,
  RightControls
} from "./styles";

import Play from "./Play";
import Cast from "./Cast";
import Volume from "./Volume";
import Fullscreen from "./Fullscreen";
import { TimeLeft } from "./TimeLeft";
import Progress from "./Progress";
import Subtitles from "./Subtitles";
import Header from "./Header";
import IPhoneOverlay from "./IPhoneOverlay/index";


const Controls = ({
  title,
  description,
  isPlaying,
  volume,
  currentSubtitle,
  subtitles,
  played,
  duration,
  timeLeft,
  onVolume,
  onSubtitles,
  onScrub,
  onPlay,
  onPause,
  onClose,
  onCast,
  isCastAvailable,
  onShowControls,
  mode,
  isFirstPlay,
}) => {

  const [isVisible, setIsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const controlsTimeoutRef = useRef()
  const volumeRef = useRef()
  const prevVolumeRef = useRef()
  const isPlayingRef = useRef()
  const playedRef = useRef()

  volumeRef.current = volume;
  isPlayingRef.current = isPlaying;
  playedRef.current = played;

  const isIPhone = isMobileOnly && isIOS
  const isAdMode = mode === 'ad'

  const handleControlsTimeout = () => {
    if (isAdMode) return

    clearInterval(controlsTimeoutRef.current);

    controlsTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      clearInterval(controlsTimeoutRef.current);
    }, 3000);
  }

  const handleKeepVisibility = (e) => {
    setIsVisible(true)
    handleControlsTimeout()
  }

  const handleClickBackdrop = () => {
    if (isAdMode) return

    if (isDesktop) {
      handleKeepVisibility()
      if (isFirstPlay) return
      if (isPlayingRef.current) onPause()
      else onPlay()

    } else {
      setIsVisible(prev => !prev)
      handleControlsTimeout()
    }
  }

  const handleClose = () => {
    if (isFullscreen) handleFullscreen()
    onClose()
  }

  const handlePlayPause = () => {
    if (isPlayingRef.current) onPause()
    else onPlay()
  }

  const increaseVolume = () => {
    const next = parseFloat((volumeRef.current + 0.1).toFixed(2))
    if (next > 1) onVolume(1)
    else onVolume(next)
  }

  const decreaseVolume = () => {
    const next = parseFloat((volumeRef.current - 0.1).toFixed(2))
    if (next <= 0) onVolume(0)
    else onVolume(next)
  }

  const muteVolume = () => {
    const newValue = volumeRef.current === 0
      ? prevVolumeRef.current || 0.8
      : 0

    prevVolumeRef.current = volumeRef.current
    onVolume(newValue)
  }

  const handleFullscreen = () => {
    screenfull.toggle()
    setIsFullscreen(isFull => !isFull);
  }

  const handleScrub = (value) => {
    if (isAdMode) return
    onScrub(value)
  }

  const scrubBack = () => handleScrub(playedRef.current - 10)

  const scrubForward = () => handleScrub(playedRef.current + 10)

  const handleShortcuts = ({ code, target }) => {

    if (target.tagName === 'BUTTON') return

    if (code === 'Escape') handleClose()
    if (code === 'KeyF') handleFullscreen()

    if (isAdMode) return

    if (code === 'Space') handlePlayPause()
    if (code === 'ArrowUp') increaseVolume()
    if (code === 'ArrowDown') decreaseVolume()
    if (code === 'ArrowLeft') scrubBack()
    if (code === 'ArrowRight') scrubForward()
    if (code === 'KeyM') muteVolume()
    
    handleKeepVisibility()
  }

  useEffect(() => {
    onShowControls(isVisible)
  }, [isVisible])

  useEffect(() => {
    window.addEventListener('keydown', handleShortcuts)
    return () => {
      window.removeEventListener('keydown', handleShortcuts)
      clearInterval(controlsTimeoutRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleKeepVisibility()
  }, [title, description])
  

  if (!isVisible) {
    return (
      <Backdrop
        key='1'
        onPointerUpCapture={handleClickBackdrop}
        onMouseMove={() => {
          if (isDesktop) handleKeepVisibility()
        }}
      />
    )
  }

  if (isAdMode) return (
    <>
      <Backdrop
        key='2'
        onPointerUpCapture={handleClickBackdrop} 
        onMouseMove={() => {
          if (isDesktop) handleKeepVisibility()
        }}
      />

      { isIPhone && isFirstPlay && (
        <IPhoneOverlay onPlay={onPlay} />
      )}

      <Header
        title={title}
        description={description}
        onClose={handleClose}
      />

      <ControlsBottom
        onPointerUp={handleKeepVisibility}
        onPointerMove={handleKeepVisibility}
        height={10}
      >
        <Progress
          max={duration}
          value={played}
          onChange={handleScrub}
        />
      </ControlsBottom>
    </>
  )

  return (
    <>
      <Backdrop
        key='3'
        onPointerUpCapture={handleClickBackdrop} 
        onMouseMove={() => {
          if (isDesktop) handleKeepVisibility()
        }}
      />

      <Header
        title={title}
        description={description}
        onClose={handleClose}
      />

      <ControlsBottom
        onPointerUp={handleKeepVisibility}
        onPointerMove={handleKeepVisibility}
        height={80}
        style={{ visibility: isFirstPlay ? 'hidden': 'visible' }}
      >
        <Progress
          max={duration}
          value={played}
          onChange={handleScrub}
        />

        <LeftControls>
          <Play isPlaying={isPlaying} onClick={handlePlayPause} />
          <Volume value={volume} onChange={onVolume} onMute={muteVolume} />
          <TimeLeft>{`-${timeLeft}`}</TimeLeft>
        </LeftControls>

        <RightControls>
          {isCastAvailable && (
            <Cast onClick={onCast} />
          )}

          {!!subtitles?.length && (
            <Subtitles
              current={currentSubtitle}
              subtitles={subtitles}
              onChange={onSubtitles}
            />
          )}

          {isIPhone
            ? null
            : <Fullscreen isFullscreen={isFullscreen} onClick={handleFullscreen} />
          }
        </RightControls>
      </ControlsBottom>
    </>
  )
}

export default Controls
